import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Alert, 
  Paper,
  CircularProgress,
  Link
} from '@mui/material';
import { resetPasswordDirect } from '../../services/authService';

/**
 * Reset Password component for direct password reset functionality
 * Allows users to reset password using username, email, and new password
 * @returns {JSX.Element} Reset Password component
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  
  // State for reset form
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    
    // Validate inputs
    if (!username.trim()) {
      setError('Username is required');
      setLoading(false);
      return;
    }
    
    if (!email.trim()) {
      setError('Email is required');
      setLoading(false);
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }
    
    if (!newPassword) {
      setError('New password is required');
      setLoading(false);
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    console.log('Direct password reset for user:', username);
    
    try {
      await resetPasswordDirect(username, email, newPassword);
      setSuccess('Your password has been reset successfully. You can now log in with your new password.');
      
      // Clear form
      setUsername('');
      setEmail('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Password reset error:', err);
      
      if (err.message.includes('User not found')) {
        setError('No account found with the provided username and email combination.');
      } else if (err.message.includes('Invalid credentials')) {
        setError('The username and email combination is incorrect.');
      } else if (err.message.includes('network') || err.message.includes('fetch')) {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        setError(err.message || 'An error occurred while resetting your password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      minHeight="100vh"
      bgcolor="#f5f5f5"
    >
      <Paper elevation={3} style={{ maxWidth: 400, width: '100%' }}>
        <Card>
          <CardContent>
            <Box textAlign="center" mb={3}>
              <Typography variant="h4" component="h1" gutterBottom>
                Reset Password
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Enter your username, email, and new password to reset your password
              </Typography>
            </Box>
            
            {error && (
              <Alert severity="error" style={{ marginBottom: 16 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" style={{ marginBottom: 16 }}>
                {success}
              </Alert>
            )}
            
            <form onSubmit={handleResetPassword}>
              <TextField
                label="Username"
                variant="outlined"
                fullWidth
                margin="normal"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                disabled={loading}
                placeholder="Enter your username"
              />
              <TextField
                label="Email Address"
                type="email"
                variant="outlined"
                fullWidth
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder="Enter your email address"
              />
              <TextField
                label="New Password"
                type="password"
                variant="outlined"
                fullWidth
                margin="normal"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={loading}
                helperText="Password must be at least 6 characters long"
              />
              <TextField
                label="Confirm New Password"
                type="password"
                variant="outlined"
                fullWidth
                margin="normal"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading}
                style={{ marginTop: 24 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Reset Password'}
              </Button>
            </form>
            
            <Box mt={2} textAlign="center">
              <Typography variant="body2">
                Remember your password?{' '}
                <Link href="/login" underline="hover">
                  Back to Login
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Paper>
    </Box>
  );
};

export default ResetPassword;
